/* customized primeNG messages component */
import { animate, state, style, transition, trigger } from '@angular/animations';
import { computed, DestroyRef, effect, EventEmitter, inject, input, NgZone, QueryList, signal, TemplateRef } from '@angular/core';
import { handleDuplicateAppMessages, LocalMessageHandlerService, takeWhileAlive } from '@core/frontend-shared';
import { PrimeTemplate } from 'prime-ng-old/api';
import { Subject, takeUntil } from 'rxjs';
import { AppMessageComponent } from './app-message.component';
import * as i0 from "@angular/core";
import * as i1 from "@core/frontend-shared";
const _c0 = ["message"];
const _c1 = (a0, a1) => ({
  showTransitionParams: a0,
  hideTransitionParams: a1
});
const _c2 = a0 => ({
  value: "visible",
  params: a0
});
function AppMessagesComponent_Conditional_0_For_2_Template(rf, ctx) {
  if (rf & 1) {
    const _r1 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "app-message", 4, 0);
    i0.ɵɵlistener("onClose", function AppMessagesComponent_Conditional_0_For_2_Template_app_message_onClose_0_listener($event) {
      i0.ɵɵrestoreView(_r1);
      const ctx_r1 = i0.ɵɵnextContext(2);
      return i0.ɵɵresetView(ctx_r1.onMessageClose($event));
    });
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const msg_r3 = ctx.$implicit;
    const ɵ$index_4_r4 = ctx.$index;
    const ctx_r1 = i0.ɵɵnextContext(2);
    i0.ɵɵproperty("message", msg_r3)("index", ɵ$index_4_r4)("@messageAnimation", i0.ɵɵpureFunction1(8, _c2, i0.ɵɵpureFunction2(5, _c1, ctx_r1.showTransitionOptions(), ctx_r1.hideTransitionOptions())))("pauseLifeTime", true)("autoClose", false);
  }
}
function AppMessagesComponent_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementStart(0, "div", 2);
    i0.ɵɵrepeaterCreate(1, AppMessagesComponent_Conditional_0_For_2_Template, 2, 10, "app-message", 3, i0.ɵɵrepeaterTrackByIdentity);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵclassMap(ctx_r1.styleClass());
    i0.ɵɵadvance();
    i0.ɵɵrepeater(ctx_r1.messages());
  }
}
/**
 * use LocalMessageHandlerService exported from @core/frontend-shared
 * if you want to display app messages in a local <app-messages /> Component instead of global message toast.
 * Note that it must be provided on component level to function as expected!
 */
export let AppMessagesComponent = /*#__PURE__*/(() => {
  class AppMessagesComponent {
    constructor(messageService, zone) {
      this.messageService = messageService;
      this.zone = zone;
      this.destroyRef = inject(DestroyRef);
      this.messages = signal([], ...(ngDevMode ? [{
        debugName: "messages"
      }] : /* istanbul ignore next */[]));
      this.limit = input(0, ...(ngDevMode ? [{
        debugName: "limit"
      }] : /* istanbul ignore next */[]));
      this.filterByKey = input(null, {
        ...(ngDevMode ? {
          debugName: "filterByKey"
        } : /* istanbul ignore next */{}),
        alias: 'key'
      });
      this.hasMessages = computed(() => this.messages()?.length > 0, ...(ngDevMode ? [{
        debugName: "hasMessages"
      }] : /* istanbul ignore next */[]));
      this.styleClass = input(...(ngDevMode ? [undefined, {
        debugName: "styleClass"
      }] : /* istanbul ignore next */[]));
      this.enableService = input(true, ...(ngDevMode ? [{
        debugName: "enableService"
      }] : /* istanbul ignore next */[]));
      this.handler = input(...(ngDevMode ? [undefined, {
        debugName: "handler"
      }] : /* istanbul ignore next */[]));
      this._onSetHandler = effect(() => {
        const handler = this.handler();
        if (!handler) return; // effect will fire even if setting no custom handler!
        this.endSubscriptions.next(true);
        this.messageService = handler;
        this.subscribe();
      }, ...(ngDevMode ? [{
        debugName: "_onSetHandler"
      }] : /* istanbul ignore next */[]));
      this.showTransitionOptions = input('300ms ease-out', ...(ngDevMode ? [{
        debugName: "showTransitionOptions"
      }] : /* istanbul ignore next */[]));
      this.hideTransitionOptions = input('250ms ease-in', ...(ngDevMode ? [{
        debugName: "hideTransitionOptions"
      }] : /* istanbul ignore next */[]));
      this.valueChange = new EventEmitter();
      this.onClose = new EventEmitter();
      this.endSubscriptions = new Subject();
    }
    ngOnInit() {
      if (this.messageService && this.enableService()) this.subscribe();
    }
    ngAfterContentInit() {
      this.templates.forEach(item => {
        this.template = item.template;
      });
    }
    subscribe() {
      if (!this.messageService) throw new Error('AppMessagesComponent.subscribe failed: no messageService was assigned/injected!');
      this.messageService.messageObserver.pipe(takeWhileAlive(this.destroyRef), takeUntil(this.endSubscriptions)).subscribe(messages => {
        this.zone.run(() => {
          if (messages) {
            const limit = this.limit();
            const filteredMessages = messages.filter(m => this.filterByKey() === m.key && !m.handled);
            const limitedMessages = limit > 0 ? filteredMessages.slice(-1 * limit) : filteredMessages;
            limitedMessages.forEach(msg => msg.setHandled());
            const previousMessages = this.messages() || [];
            const groupedMessages = handleDuplicateAppMessages([...previousMessages, ...limitedMessages]);
            this.messages.set(groupedMessages);
          }
        });
      });
      this.messageService.clearObserver.pipe(takeWhileAlive(this.destroyRef), takeUntil(this.endSubscriptions)).subscribe(key => {
        this.zone.run(() => {
          if (key) {
            if (this.filterByKey() === key) this.messages.set([]);
          } else {
            this.messages.set([]);
          }
        });
      });
    }
    onMessageClose(event) {
      const cleanedMessageStack = this.messages().filter((m, i) => i !== event.index);
      this.messages.set(cleanedMessageStack);
      this.onClose.emit({
        message: event.message
      });
    }
    clear(event) {
      this.messages.set([]);
      this.valueChange.emit(this.messages());
      event.preventDefault();
    }
    static {
      this.ɵfac = function AppMessagesComponent_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || AppMessagesComponent)(i0.ɵɵdirectiveInject(i1.LocalMessageHandlerService), i0.ɵɵdirectiveInject(i0.NgZone));
      };
    }
    static {
      this.ɵcmp = /*@__PURE__*/i0.ɵɵdefineComponent({
        type: AppMessagesComponent,
        selectors: [["app-messages"]],
        contentQueries: function AppMessagesComponent_ContentQueries(rf, ctx, dirIndex) {
          if (rf & 1) {
            i0.ɵɵcontentQuery(dirIndex, PrimeTemplate, 4);
          }
          if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.templates = _t);
          }
        },
        viewQuery: function AppMessagesComponent_Query(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵviewQuery(_c0, 5);
          }
          if (rf & 2) {
            let _t;
            i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.messageComponents = _t);
          }
        },
        inputs: {
          limit: [1, "limit"],
          filterByKey: [1, "key", "filterByKey"],
          styleClass: [1, "styleClass"],
          enableService: [1, "enableService"],
          handler: [1, "handler"],
          showTransitionOptions: [1, "showTransitionOptions"],
          hideTransitionOptions: [1, "hideTransitionOptions"]
        },
        outputs: {
          valueChange: "valueChange",
          onClose: "onClose"
        },
        decls: 1,
        vars: 1,
        consts: [["message", ""], [1, "p-messages", "p-component", 3, "class"], [1, "p-messages", "p-component"], [3, "message", "index", "pauseLifeTime", "autoClose"], [3, "onClose", "message", "index", "pauseLifeTime", "autoClose"]],
        template: function AppMessagesComponent_Template(rf, ctx) {
          if (rf & 1) {
            i0.ɵɵconditionalCreate(0, AppMessagesComponent_Conditional_0_Template, 3, 2, "div", 1);
          }
          if (rf & 2) {
            i0.ɵɵconditional(ctx.hasMessages() ? 0 : -1);
          }
        },
        dependencies: [AppMessageComponent],
        styles: [".p-messages[_ngcontent-%COMP%]{margin:1em 0;text-align:left}"],
        data: {
          animation: [trigger('messageAnimation', [state('visible', style({
            transform: 'translateY(0)',
            opacity: 1
          })), transition('void => *', [style({
            transform: 'translateY(-25%)',
            opacity: 0
          }), animate('{{showTransitionParams}}')]), transition('* => void', [animate('{{hideTransitionParams}}', style({
            opacity: 0,
            transform: 'translateY(-25%)'
          }))])])]
        },
        changeDetection: 0
      });
    }
  }
  return AppMessagesComponent;
})();